import {
  DateRange,
  GetStatisticsDataQuery,
  GetStatisticsDataQueryVariables,
  StatisticsDataInput,
  eventType,
} from "../../../../../API";
import { NetworkStatus, useQuery } from "@apollo/client";

import { GET_STATISTICS_DATA } from "../../../../../common/operations/queries";
import { IStatisticsResponseModel } from "../../system-performance/models/chart";
import { parseQueryResult } from "../../../../../common/utils/timestreamHelper";
import { useCustomerIdGuard } from "../../../../../common/hooks/useCustomerIdGuard";
import { useDateRange } from "../../../../../common/components/datePicker/modelsDatePickers";
import { useMemo } from "react";

export const useGetStatisticsData = <T>(
  input: Pick<
    StatisticsDataInput,
    | "eventType"
    | "models"
    | "timePeriod"
    | "locations"
    | "timeframeDays"
    | "jobId"
  >
) => {
  const selectedCustomerId = useCustomerIdGuard();
  const dateRange = useDateRange();

  const defaultInput = {
    customerId: selectedCustomerId,
    ...input,
  };

  const { data, loading, refetch, networkStatus } = useQuery<
    GetStatisticsDataQuery,
    GetStatisticsDataQueryVariables
  >(GET_STATISTICS_DATA, {
    variables: { input: defaultInput },
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    skip: !selectedCustomerId,
  });

  const refetchWithChosenValues = () => {
    refetchChartData(input.locations, input.timeframeDays ?? 14);
  };

  const refetchChartData = (
    locations?: string[] | null,
    timeframeDays?: number
  ) => {
    const inputModel = {
      customerId: selectedCustomerId,
      eventType: input.eventType,
      locations,
      timeframeDays,
      models: input.models,
    };

    refetch({ input: inputModel });
  };

  const refetchWithSpecificData = (deviceData: IStatisticsResponseModel) => {
    const inputModel = {
      customerId: selectedCustomerId,
      eventType: eventType.deviceOverview,
      locations: input.locations,
      timeframeDays: input.timeframeDays,
      deviceName: deviceData.deviceName,
      date: `${deviceData.date}`,
      dateRange: dateRange as DateRange,
    };

    refetch({ input: inputModel });
  };

  const parsed: T = useMemo(
    () =>
      data?.getStatisticsData
        ? parseQueryResult(JSON.parse(data.getStatisticsData ?? ""))
        : [],
    [data?.getStatisticsData]
  );

  return {
    data: parsed,
    loading,
    refetching: networkStatus === NetworkStatus.refetch,
    refetchChartData,
    refetchWithSpecificData,
    refetchWithChosenValues,
  };
};
